import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useQuery } from 'react-query'
import { useAuth } from '@/lib/auth'
import { useTranslation } from 'react-i18next'
import { authApi } from '@/lib/api'
import LanguageToggle from '@/components/LanguageToggle'

export default function SettingsPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('common')
  const [copied, setCopied] = useState(false)

  const { data: referralInfo } = useQuery(['referral-info'], () => authApi.getReferralInfo(), {
    staleTime: 60000,
  })

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  const handleCopyCode = async () => {
    if (referralInfo?.today_code) {
      await navigator.clipboard.writeText(referralInfo.today_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold text-olive-900 mb-8">{t('settings.title')}</h1>

      {/* Profile Section */}
      <section className="card mb-6">
        <h2 className="text-xl font-semibold text-olive-900 mb-4">{t('settings.profile.title')}</h2>
        <div className="space-y-4">
          <ProfileRow label={t('settings.profile.username')} value={user?.username} />
          <ProfileRow
            label={t('settings.profile.email')}
            value={user?.email || t('settings.profile.notSet')}
          />
          {user?.phone && <ProfileRow label={t('settings.profile.phone')} value={user.phone} />}
          <ProfileRow label={t('settings.profile.role')} value={user?.role || 'contributor'} />
          {user?.created_at && (
            <ProfileRow
              label={t('settings.profile.memberSince')}
              value={formatDate(user.created_at)}
            />
          )}
        </div>
      </section>

      {/* Referral Section */}
      {referralInfo && (
        <section className="card mb-6">
          <h2 className="text-xl font-semibold text-olive-900 mb-4">
            {t('settings.referral.title')}
          </h2>
          <div className="bg-olive-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-olive-600 mb-2">{t('settings.referral.todayCode')}</p>
            <div className="flex items-center gap-3">
              <code className="flex-1 px-4 py-2 bg-white border border-olive-200 rounded-lg font-mono text-lg text-olive-900 tracking-wider">
                {referralInfo.today_code}
              </code>
              <button
                onClick={handleCopyCode}
                className="px-4 py-2 text-sm font-medium bg-olive-600 text-white rounded-lg hover:bg-olive-700 transition-colors"
              >
                {copied ? t('settings.referral.copied') : t('settings.referral.copy')}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="p-3 bg-olive-50 rounded-lg">
              <p className="text-2xl font-bold text-olive-900">{referralInfo.total_referrals}</p>
              <p className="text-sm text-olive-600">{t('settings.referral.totalReferrals')}</p>
            </div>
            <div className="p-3 bg-olive-50 rounded-lg">
              <p className="text-2xl font-bold text-olive-900">{referralInfo.active_codes}</p>
              <p className="text-sm text-olive-600">{t('settings.referral.activeCodes')}</p>
            </div>
          </div>
        </section>
      )}

      {/* Language Section */}
      <section className="card mb-6">
        <h2 className="text-xl font-semibold text-olive-900 mb-4">
          {t('settings.language.title')}
        </h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-olive-700">{t('settings.language.current')}</p>
            <p className="text-sm text-olive-500">
              {i18n.language === 'ar'
                ? t('settings.language.arabic')
                : t('settings.language.english')}
            </p>
          </div>
          <LanguageToggle />
        </div>
      </section>

      {/* Keyboard Shortcuts Section */}
      <section className="card mb-6">
        <h2 className="text-xl font-semibold text-olive-900 mb-4">
          {t('settings.shortcuts.title')}
        </h2>
        <p className="text-olive-600 mb-4">{t('settings.shortcuts.description')}</p>
        <div className="bg-olive-50 rounded-lg p-4 space-y-3">
          <ShortcutRow shortcut="Ctrl+K" description={t('settings.shortcuts.search')} />
          <ShortcutRow shortcut="G U" description={t('settings.shortcuts.upload')} />
          <ShortcutRow shortcut="G D" description={t('settings.shortcuts.dashboard')} />
          <ShortcutRow shortcut="Esc" description={t('settings.shortcuts.close')} />
        </div>
        <p className="text-xs text-olive-500 mt-3">{t('settings.shortcuts.comingSoon')}</p>
      </section>

      {/* Account Section */}
      <section className="card">
        <h2 className="text-xl font-semibold text-olive-900 mb-4">{t('settings.account.title')}</h2>
        <div className="space-y-4">
          <button
            onClick={handleLogout}
            className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium bg-olive-100 text-olive-700 rounded-lg hover:bg-olive-200 transition-colors"
          >
            {t('nav.logout')}
          </button>
        </div>
      </section>
    </div>
  )
}

function ProfileRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 border-b border-olive-100 last:border-0">
      <span className="text-sm text-olive-600">{label}</span>
      <span className="text-olive-900 font-medium">{value || '-'}</span>
    </div>
  )
}

function ShortcutRow({ shortcut, description }: { shortcut: string; description: string }) {
  const { t } = useTranslation('common')
  const parts = shortcut.split(/([+ ])/)
  return (
    <div className="flex items-center justify-between">
      <span className="text-olive-700">{description}</span>
      <div className="flex items-center gap-1 ltr">
        {parts.map((part, i) => {
          if (part === '+') {
            return (
              <span key={i} className="text-olive-400">
                +
              </span>
            )
          }
          if (part === ' ') {
            return (
              <span key={i} className="text-olive-400 text-xs mx-0.5">
                {t('settings.shortcuts.then')}
              </span>
            )
          }
          return (
            <kbd
              key={i}
              className="px-2 py-1 text-xs font-mono bg-white border border-olive-200 rounded shadow-sm"
            >
              {part}
            </kbd>
          )
        })}
      </div>
    </div>
  )
}
